/**
 * The end-of-life codes in words — `docs/adr/7003`.
 *
 * Every code below is read off bata-platform's own source at `b0a2ec3d` (v0.77.0):
 * `packages/planning/exit/transition.py` (ADR 7026), `packages/planning/exit/withdrawal.py`
 * (ADR 7027) and `packages/planning/markdown/phase.py`. The spec types most of them as bare
 * strings, so a code this build has no words for is shown as the code itself, inside a sentence
 * that says it is one ({@link codeText}) — never guessed at, never folded into a known one.
 *
 * The recommendation reasons and the planner's decision reasons are GOVERNED vocabularies
 * (registry rows `exit.lifecycle_transition.reason_code_vocabulary` and the decision-reason row):
 * the platform says which codes are in force, and this map only labels them. A label here is not a
 * permission — the decision form offers exactly the codes `GET …/decision-reasons` returns.
 */

import type { TFunction } from 'i18next';

import { formatCurrency, formatNumber } from '@/lib/format';
import { wordKey } from '@/planning/words';

export { wordKey };

/** The three phases (owner ruling 2026-10-08): MAIN → ZONA_SALES → OUTLET. */
export const PHASE_KEY: Readonly<Record<string, string>> = {
  MAIN: 'exit.lifecycle.phase.main',
  ZONA_SALES: 'exit.lifecycle.phase.zonaSales',
  OUTLET: 'exit.lifecycle.phase.outlet',
};

/** Where the phase in force came from: a planner's decision, or bata-data's inference. */
export const PHASE_SOURCE_KEY: Readonly<Record<string, string>> = {
  decision: 'exit.lifecycle.phaseSource.decision',
  inferred: 'exit.lifecycle.phaseSource.inferred',
};

/** The recommendation's reasons — the five ordinary signals and the three CORE/NOOS end-of-life ones. */
export const SIGNAL_KEY: Readonly<Record<string, string>> = {
  excess_cover: 'exit.lifecycle.signal.excessCover',
  aged: 'exit.lifecycle.signal.aged',
  size_run_broken: 'exit.lifecycle.signal.sizeRunBroken',
  markdown_deepening: 'exit.lifecycle.signal.markdownDeepening',
  behind_curve: 'exit.lifecycle.signal.behindCurve',
  carry_over_dropped: 'exit.lifecycle.signal.carryOverDropped',
  curve_exhausted: 'exit.lifecycle.signal.curveExhausted',
  protected_deep_markdown: 'exit.lifecycle.signal.protectedDeepMarkdown',
};

/** The unit each signal's value and threshold are in — what the number next to it means. */
export const SIGNAL_UNIT_KEY: Readonly<Record<string, string>> = {
  excess_cover: 'exit.lifecycle.signalUnit.weeks',
  aged: 'exit.lifecycle.signalUnit.weeks',
  size_run_broken: 'exit.lifecycle.signalUnit.share',
  markdown_deepening: 'exit.lifecycle.signalUnit.share',
  behind_curve: 'exit.lifecycle.signalUnit.share',
  curve_exhausted: 'exit.lifecycle.signalUnit.weeks',
  protected_deep_markdown: 'exit.lifecycle.signalUnit.share',
};

/** A signal read: fired, not fired, or not measured (and why). */
export const SIGNAL_STATE_KEY: Readonly<Record<string, string>> = {
  fired: 'exit.lifecycle.signalState.fired',
  not_fired: 'exit.lifecycle.signalState.notFired',
  not_measured: 'exit.lifecycle.signalState.notMeasured',
};

/** Why a signal was not measured. */
export const ABSENT_REASON_KEY: Readonly<Record<string, string>> = {
  stock_not_measured: 'exit.lifecycle.absent.stockNotMeasured',
  demand_not_positive: 'exit.lifecycle.absent.demandNotPositive',
  first_sale_not_landed: 'exit.lifecycle.absent.firstSaleNotLanded',
  no_stocked_door: 'exit.lifecycle.absent.noStockedDoor',
  price_panel_insufficient: 'exit.lifecycle.absent.pricePanelInsufficient',
  list_price_absent: 'exit.lifecycle.absent.listPriceAbsent',
  lifecycle_curve_absent: 'exit.lifecycle.absent.lifecycleCurveAbsent',
  curve_not_assigned: 'exit.lifecycle.absent.curveNotAssigned',
  carry_over_history_absent: 'exit.lifecycle.absent.carryOverHistoryAbsent',
};

/** Whether the CORE / NOOS rule applied. */
export const PROTECTION_KEY: Readonly<Record<string, string>> = {
  unprotected: 'exit.lifecycle.protection.unprotected',
  protected_end_of_life: 'exit.lifecycle.protection.endOfLife',
};

/** A door's part in the move (ADR 7026): donor, recipient, unknown area, another channel. */
export const DOOR_ROLE_KEY: Readonly<Record<string, string>> = {
  donor: 'exit.lifecycle.role.donor',
  recipient: 'exit.lifecycle.role.recipient',
  unknown: 'exit.lifecycle.role.unknown',
  other_channel: 'exit.lifecycle.role.otherChannel',
};

/** The planner's answer. */
export const DISPOSITION_KEY: Readonly<Record<string, string>> = {
  accept: 'exit.lifecycle.disposition.accept',
  reject: 'exit.lifecycle.disposition.reject',
};

/**
 * The planner's decision reasons — the proposed starter set of payload `2026-10-09-lifecycle-
 * transition/02`, which the owner may narrow or rename. A code the row adds is shown as itself.
 */
export const DECISION_REASON_KEY: Readonly<Record<string, string>> = {
  evidence_confirmed: 'exit.lifecycle.decisionReason.evidenceConfirmed',
  end_of_season_clearance: 'exit.lifecycle.decisionReason.endOfSeasonClearance',
  stock_still_needed_in_main: 'exit.lifecycle.decisionReason.stockStillNeededInMain',
  promotion_planned: 'exit.lifecycle.decisionReason.promotionPlanned',
  evidence_disputed: 'exit.lifecycle.decisionReason.evidenceDisputed',
};

/** What one accepted move's withdrawal did (ADR 7027). */
export const WITHDRAWAL_OUTCOME_KEY: Readonly<Record<string, string>> = {
  lines_written: 'exit.lifecycle.withdrawalOutcome.linesWritten',
  withdrawal_already_written: 'exit.lifecycle.withdrawalOutcome.alreadyWritten',
  no_stock_to_withdraw: 'exit.lifecycle.withdrawalOutcome.noStock',
  no_recipient_in_range: 'exit.lifecycle.withdrawalOutcome.noRecipient',
};

/** What happened at one door the withdrawal named. */
export const WITHDRAWAL_DOOR_OUTCOME_KEY: Readonly<Record<string, string>> = {
  moved: 'exit.lifecycle.doorOutcome.moved',
  no_recipient_in_range: 'exit.lifecycle.doorOutcome.noRecipient',
  stock_not_measured: 'exit.lifecycle.doorOutcome.stockNotMeasured',
  no_stock_at_execution: 'exit.lifecycle.doorOutcome.noStockAtExecution',
  unknown_zone_door: 'exit.lifecycle.doorOutcome.unknownZoneDoor',
};

/** A code in words; one this build does not know is said as the code it is. */
export function codeText(t: TFunction, map: Readonly<Record<string, string>>, code: string): string {
  const key = wordKey(map, code);
  return key === null ? t('exit.lifecycle.unknownCode', { code }) : t(key);
}

/** A whole-run refusal or an absent input, named: the platform's code, inside a sentence. */
export function runCodeText(t: TFunction, code: string): string {
  return t('exit.lifecycle.runCode', { code });
}

/** A recommendation's reasons in words, in the order the platform gave them. */
export function reasonsText(row: { readonly reasonCodes: readonly string[] }, t: TFunction): string {
  return row.reasonCodes.map((code) => codeText(t, SIGNAL_KEY, code)).join(' · ');
}

// ── the outlet exit (bata-reqs issue 35 P4, bata-platform ADR 7028, v0.77.0 main eeadf78) ──────
// Read off `packages/planning/exit/outlet_exit.py` and `services/repositories/lifecycle_outlet.py`.

/** The two reasons (and the two signals): the stay has run out (forced), or the outlet earns more. */
export const OUTLET_REASON_KEY: Readonly<Record<string, string>> = {
  stay_elapsed: 'exit.lifecycle.outlet.reason.stayElapsed',
  outlet_value_exceeds_stay: 'exit.lifecycle.outlet.reason.outletValue',
};

/** The forced reason: an article whose maximum stay has run out goes, whatever the comparison. */
export const OUTLET_FORCED_REASON = 'stay_elapsed';

/** A recommendation's reasons, the forced one first, in words. */
export function outletReasonsText(row: { readonly reasonCodes: readonly string[] }, t: TFunction): string {
  const ordered = [...row.reasonCodes].sort((a, b) => Number(b === OUTLET_FORCED_REASON) - Number(a === OUTLET_FORCED_REASON));
  return ordered.map((code) => codeText(t, OUTLET_REASON_KEY, code)).join(' · ');
}

/** Why a reading, a price or a comparison side was not measured. */
export const OUTLET_ABSENT_KEY: Readonly<Record<string, string>> = {
  zona_sales_entry_unknown: 'exit.lifecycle.outlet.absent.entryUnknown',
  original_price_absent: 'exit.lifecycle.outlet.absent.originalPrice',
  outlet_price_not_resolvable: 'exit.lifecycle.outlet.absent.outletPrice',
  stay_price_absent: 'exit.lifecycle.outlet.absent.stayPrice',
  stay_demand_not_measured: 'exit.lifecycle.outlet.absent.stayDemand',
  no_outlet_door: 'exit.lifecycle.outlet.absent.noOutletDoor',
  outlet_demand_not_measured: 'exit.lifecycle.outlet.absent.outletDemand',
  no_outlet_door_in_range: 'exit.lifecycle.outlet.absent.noOutletInRange',
};

/** What the stay side was valued on. */
export const STAY_BASIS_KEY: Readonly<Record<string, string>> = {
  price_path: 'exit.lifecycle.outlet.stayBasis.pricePath',
  ledger_demand_current_price: 'exit.lifecycle.outlet.stayBasis.ledger',
};

/** What the original price is. */
export const ORIGINAL_PRICE_BASIS_KEY: Readonly<Record<string, string>> = {
  panel_list_price: 'exit.lifecycle.outlet.originalBasis.panelListPrice',
};

/** How an outlet door's rate was read. */
export const RATE_BASIS_KEY: Readonly<Record<string, string>> = {
  article_at_door: 'exit.lifecycle.outlet.rateBasis.articleAtDoor',
  outlet_door_analog: 'exit.lifecycle.outlet.rateBasis.analog',
};

/** A door's part in the move to the outlet. */
export const OUTLET_DOOR_ROLE_KEY: Readonly<Record<string, string>> = {
  donor: 'exit.lifecycle.outlet.role.origin',
  unknown: 'exit.lifecycle.outlet.role.unknown',
  outlet: 'exit.lifecycle.outlet.role.outlet',
};

/** What happened at a door: an origin's outcome as the withdrawal's, or an outlet that receives or is not used. */
export const OUTLET_DOOR_OUTCOME_KEY: Readonly<Record<string, string>> = {
  ...WITHDRAWAL_DOOR_OUTCOME_KEY,
  unknown_zone_door: 'exit.lifecycle.outlet.doorOutcome.unknownDoor',
  receives: 'exit.lifecycle.outlet.doorOutcome.receives',
  not_used: 'exit.lifecycle.outlet.doorOutcome.notUsed',
};

/** The planner's decision reasons — payload `2026-10-10-outlet-exit/02`'s proposed starter set. */
export const OUTLET_DECISION_REASON_KEY: Readonly<Record<string, string>> = {
  evidence_confirmed: 'exit.lifecycle.decisionReason.evidenceConfirmed',
  stay_limit_reached: 'exit.lifecycle.outlet.decisionReason.stayLimitReached',
  outlet_capacity_short: 'exit.lifecycle.outlet.decisionReason.outletCapacityShort',
  still_selling_in_zona_sales: 'exit.lifecycle.outlet.decisionReason.stillSelling',
  evidence_disputed: 'exit.lifecycle.decisionReason.evidenceDisputed',
};

/** An input the outlet run could not read, without refusing. */
export const OUTLET_UNAVAILABLE_KEY: Readonly<Record<string, string>> = {
  price_observation: 'exit.lifecycle.outlet.unavailable.pricePanel',
  'markdown.list_price_quantile': 'exit.lifecycle.outlet.unavailable.listPriceQuantile',
  'markdown.currency_minor_unit': 'exit.lifecycle.outlet.unavailable.minorUnit',
  inseason_price_path_week: 'exit.lifecycle.outlet.unavailable.pricePath',
  'location_master.outlet_doors': 'exit.lifecycle.outlet.unavailable.outletDoors',
  exit_lane_rate_card: 'exit.lifecycle.outlet.unavailable.rateCard',
  'location_master.capacity_units': 'exit.lifecycle.outlet.unavailable.capacity',
  product_attribute_master: 'exit.lifecycle.outlet.unavailable.attributes',
  // bata-platform ADR 7032: an outlet door with no row in the day's outlet-door position.
  'outlet_door_position.door_not_reported': 'exit.lifecycle.outlet.unavailable.doorNotReported',
};

/**
 * Whole-run refusals with words (bata-platform ADR 7032, `services/repositories/lifecycle_outlet.py`):
 * no outlet-door position in force on the run's day, for the exit and for its transfer. Every other
 * refusal is still said as its code ({@link refusalText}).
 */
export const OUTLET_REFUSAL_KEY: Readonly<Record<string, string>> = {
  'planning.exit.lifecycle_outlet.outlet_position_absent': 'exit.lifecycle.outlet.refusal.positionAbsent',
  'planning.exit.lifecycle_outlet_transfer.outlet_position_absent': 'exit.lifecycle.outlet.refusal.positionAbsent',
};

/** A run's refusal: in words where this build has them, else the platform's code. */
export function refusalText(t: TFunction, code: string): string {
  const key = wordKey(OUTLET_REFUSAL_KEY, code);
  return key === null ? code : t(key, { code });
}

/** An input a run could not read: in words where this build has them, else the code in a sentence. */
export function unavailableText(t: TFunction, input: string): string {
  const key = wordKey(OUTLET_UNAVAILABLE_KEY, input);
  return key === null ? runCodeText(t, input) : t(key);
}

/** The input whose absence leaves every outlet side unmeasured. */
export const NO_OUTLET_DOORS = 'location_master.outlet_doors';

/** Money in its currency; `null` is not measured, never zero. Pure over the locale. */
export function moneyText(value: number | null, currency: string | null, locale: string): string | null {
  if (value === null) return null;
  return currency === null ? formatNumber(value, locale) : formatCurrency(value, currency, locale);
}

/** The original price's basis in words — the platform's code, in a sentence, where this build has none. */
export function originalBasisText(basis: string | null, t: TFunction): string {
  return basis === null ? t('exit.lifecycle.outlet.notStated') : codeText(t, ORIGINAL_PRICE_BASIS_KEY, basis);
}

// ── the phase register (bata-platform ADR 7031 and ADR 7029's guard; bata-ui ADR 7005) ─────────
// Read off `packages/planning/markdown/phase.py` and `services/api/routers/exit_phases.py` at
// platform main `32e6601b`, and bata-data's `pub_exit_article_lifecycle_phase_month.sql` (ADR 7003).

/**
 * The minimum-age guard on the phase in force (platform ADR 7029). The first two put the article
 * back in the main line; the last two keep it in the Zona sale but neither priced nor moved.
 */
export const PHASE_GUARD_KEY: Readonly<Record<string, string>> = {
  inferred_below_minimum_age: 'exit.lifecycle.phases.guard.inferredBelowMinimumAge',
  decided_below_minimum_age: 'exit.lifecycle.phases.guard.decidedBelowMinimumAge',
  inferred_age_not_measured: 'exit.lifecycle.phases.guard.inferredAgeNotMeasured',
  inferred_minimum_age_not_governed: 'exit.lifecycle.phases.guard.minimumAgeNotGoverned',
};

/** The guards that hold an article in the Zona sale without pricing or moving it. */
export const GUARD_KEEPS_ZONA_SALES: ReadonlySet<string> = new Set(['inferred_age_not_measured', 'inferred_minimum_age_not_governed']);

/** Which recommendation a planner's phase decision accepted. */
export const DECISION_ORIGIN_KEY: Readonly<Record<string, string>> = {
  transition: 'exit.lifecycle.phases.origin.transition',
  outlet_exit: 'exit.lifecycle.phases.origin.outletExit',
};

/** What bata-data's inference read the month's phase from (data ADR 7003). */
export const INFERRED_BASIS_KEY: Readonly<Record<string, string>> = {
  outlet_unit_share: 'exit.lifecycle.phases.basis.outletUnitShare',
  outlet_stock_share: 'exit.lifecycle.phases.basis.outletStockShare',
  persistent_markdown: 'exit.lifecycle.phases.basis.persistentMarkdown',
  protected_end_of_life_markdown: 'exit.lifecycle.phases.basis.protectedEndOfLifeMarkdown',
  main_protected_lifecycle: 'exit.lifecycle.phases.basis.mainProtectedLifecycle',
  main_insufficient_evidence: 'exit.lifecycle.phases.basis.mainInsufficientEvidence',
  main_no_persistent_markdown: 'exit.lifecycle.phases.basis.mainNoPersistentMarkdown',
  insufficient_evidence_carried_forward: 'exit.lifecycle.phases.basis.carriedForward',
};
